import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useAuth } from "@/hooks/use-auth";
import { Redirect } from "wouter";
import { Button } from "@/components/ui/button";
import {
  Mail, Phone, ExternalLink, Globe, FileText, Loader2, X, ChevronLeft,
} from "lucide-react";

interface InspirationLink { url: string; notes: string; }

interface Brief {
  id: number;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  fbProfileLink: string | null;
  businessName: string;
  businessType: string;
  businessDescription: string;
  targetAudience: string | null;
  mainGoal: string | null;
  hasExistingWebsite: boolean;
  existingWebsiteUrl: string | null;
  inspirationLinks: string | null;
  brandStyle: string[];
  brandColourPrimary: string | null;
  brandColourSecondary: string | null;
  logoUrl: string | null;
  pagesNeeded: string[];
  features: string[];
  hasPhotos: string | null;
  hasContent: string | null;
  socialFacebook: string | null;
  socialInstagram: string | null;
  socialTiktok: string | null;
  socialOther: string | null;
  preferredDomain: string | null;
  timeline: string | null;
  additionalInfo: string | null;
  status: string;
  adminNotes: string | null;
  createdAt: string;
}

// ── Label maps ─────────────────────────────────────────────────────────────────

const FEATURE_LABELS: Record<string, string> = {
  mobile_app: "Mobile App", payments: "Online Payments", accounts: "Customer Accounts",
  ordering: "Online Ordering", bookings: "Online Bookings", bespoke: "Something Bespoke",
};
const PAGE_LABELS: Record<string, string> = {
  home: "Home", about: "About Us", services: "Services", gallery: "Gallery / Portfolio",
  shop: "Online Shop", booking: "Booking", contact: "Contact", testimonials: "Reviews",
  faq: "FAQ", blog: "Blog / News", menu: "Menu / Price List",
};
const GOAL_LABELS: Record<string, string> = {
  bookings: "Get more bookings / appointments", showcase: "Show off work / portfolio",
  sell: "Sell products online", info: "Provide business information",
  leads: "Get more enquiries / leads", community: "Build a community or membership", other: "Other",
};
const TIMELINE_LABELS: Record<string, string> = {
  asap: "ASAP!", "1_month": "Within 1 month", "3_months": "Within 3 months", flexible: "No rush — flexible",
};
const PHOTOS_LABELS: Record<string, string> = {
  yes: "Yes — will send their own photos",
  need_taken: "No — would like photos taken",
  stock_ok: "Happy to use stock photos",
};
const CONTENT_LABELS: Record<string, string> = {
  yes: "Yes — will write their own content",
  no_need_help: "No — needs help writing content",
};
const STYLE_LABELS: Record<string, string> = {
  clean_modern: "✨ Clean & modern", bold_colourful: "🎨 Bold & colourful",
  luxury_premium: "💎 Luxury & premium", friendly_fun: "😊 Friendly & fun",
  professional: "🤝 Professional & trustworthy", traditional: "🏛️ Traditional & classic",
};
const STATUS_STYLES: Record<string, string> = {
  pending:     "bg-amber-100 text-amber-700 border-amber-300",
  in_progress: "bg-blue-100 text-blue-700 border-blue-300",
  completed:   "bg-green-100 text-green-700 border-green-300",
  on_hold:     "bg-slate-100 text-slate-600 border-slate-300",
};

const token = () => localStorage.getItem("ayrshire_token") ?? "";

function fmtDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", {
    day: "numeric", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
}

// ── Shared UI bits ─────────────────────────────────────────────────────────────

function Block({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[10px] font-bold uppercase tracking-widest text-slate-400 mb-1">{label}</p>
      <div>{children}</div>
    </div>
  );
}

function Val({ v }: { v: string | null | undefined }) {
  return v
    ? <p className="text-sm text-slate-800">{v}</p>
    : <p className="text-sm text-slate-300 italic">Not provided</p>;
}

function Chips({ items }: { items: string[] }) {
  if (!items || items.length === 0)
    return <p className="text-sm text-slate-300 italic">None selected</p>;
  return (
    <div className="flex flex-wrap gap-1.5">
      {items.map(i => (
        <span key={i} className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
          {i}
        </span>
      ))}
    </div>
  );
}

function Divider() {
  return <hr className="border-slate-100" />;
}

// ── Detail panel ───────────────────────────────────────────────────────────────

function BriefDetail({ brief, onClose, onStatusChange }: {
  brief: Brief;
  onClose: () => void;
  onStatusChange: (id: number, status: string) => void;
}) {
  const [saving, setSaving] = useState(false);

  const links: InspirationLink[] = (() => {
    try { return brief.inspirationLinks ? JSON.parse(brief.inspirationLinks) as InspirationLink[] : []; }
    catch { return []; }
  })();

  async function setStatus(status: string) {
    setSaving(true);
    try {
      await fetch(`/api/admin/free-website-applications/${brief.id}/status`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token()}` },
        body: JSON.stringify({ status }),
      });
      onStatusChange(brief.id, status);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex">
      {/* Backdrop */}
      <button className="absolute inset-0 bg-black/30" onClick={onClose} />

      {/* Panel — slides in from right */}
      <div className="relative ml-auto w-full max-w-2xl bg-white h-full overflow-y-auto shadow-2xl flex flex-col">

        {/* Header */}
        <div className="sticky top-0 z-10 bg-white border-b border-slate-200 px-6 py-4 flex items-start justify-between gap-4">
          <div className="min-w-0">
            <h2 className="text-lg font-bold text-slate-800 truncate">{brief.businessName}</h2>
            <p className="text-xs text-slate-400 mt-0.5">{brief.businessType} · {fmtDate(brief.createdAt)}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600 flex-shrink-0 mt-0.5">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Status bar */}
        <div className="px-6 py-3 bg-slate-50 border-b border-slate-200 flex items-center gap-2 flex-wrap">
          <span className="text-xs font-semibold text-slate-500 mr-1">Status:</span>
          {["pending", "in_progress", "completed", "on_hold"].map(s => (
            <button key={s} type="button" disabled={saving}
              onClick={() => void setStatus(s)}
              className={`px-3 py-1 rounded-full text-xs font-semibold border transition-all ${
                brief.status === s
                  ? (STATUS_STYLES[s] ?? "bg-slate-100 text-slate-600 border-slate-300")
                  : "border-slate-200 text-slate-400 hover:border-slate-300 bg-white"
              }`}>
              {s.replace("_", " ")}
            </button>
          ))}
          {saving && <Loader2 className="w-3.5 h-3.5 animate-spin text-slate-400" />}
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-6 flex-1">

          {/* ── 1. Contact ─────────────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Contact Details</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Block label="Full name"><Val v={brief.contactName} /></Block>
              <Block label="Phone"><Val v={brief.contactPhone} /></Block>
              <Block label="Email address"><Val v={brief.contactEmail} /></Block>
              <Block label="Facebook link">
                {brief.fbProfileLink
                  ? <a href={brief.fbProfileLink} target="_blank" rel="noreferrer"
                      className="text-sm text-primary hover:underline flex items-center gap-1">
                      View <ExternalLink className="w-3 h-3" />
                    </a>
                  : <p className="text-sm text-slate-300 italic">Not provided</p>}
              </Block>
            </div>
          </section>

          <Divider />

          {/* ── 2. Business ────────────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">The Business</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Block label="Business name"><Val v={brief.businessName} /></Block>
              <Block label="Business type"><Val v={brief.businessType} /></Block>
              <Block label="Main goal">
                <Val v={brief.mainGoal ? (GOAL_LABELS[brief.mainGoal] ?? brief.mainGoal) : null} />
              </Block>
              <Block label="Target customers"><Val v={brief.targetAudience} /></Block>
              <Block label="Has existing website?">
                <Val v={brief.hasExistingWebsite ? "Yes" : "No"} />
              </Block>
              <Block label="Existing website URL"><Val v={brief.existingWebsiteUrl} /></Block>
            </div>
            <Block label="Business description">
              {brief.businessDescription
                ? <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded-xl p-4 leading-relaxed">{brief.businessDescription}</p>
                : <p className="text-sm text-slate-300 italic">Not provided</p>}
            </Block>
          </section>

          <Divider />

          {/* ── 3. Inspiration ─────────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Inspiration Links</h3>
            {links.length === 0
              ? <p className="text-sm text-slate-300 italic">No inspiration links provided</p>
              : <div className="space-y-2">
                  {links.map((l, i) => (
                    <div key={i} className="bg-slate-50 border border-slate-200 rounded-xl p-3 space-y-1">
                      <a href={l.url.startsWith("http") ? l.url : `https://${l.url}`}
                        target="_blank" rel="noreferrer"
                        className="text-sm text-primary hover:underline flex items-center gap-1.5 font-medium">
                        <Globe className="w-3.5 h-3.5 flex-shrink-0" />
                        {l.url}
                        <ExternalLink className="w-3 h-3 flex-shrink-0" />
                      </a>
                      {l.notes
                        ? <p className="text-xs text-slate-600 pl-5 italic">"{l.notes}"</p>
                        : <p className="text-xs text-slate-300 pl-5 italic">No notes</p>}
                    </div>
                  ))}
                </div>}
          </section>

          <Divider />

          {/* ── 4. Style & Brand ───────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Style & Brand</h3>

            <Block label="Style preferences">
              <Chips items={(brief.brandStyle ?? []).map(s => STYLE_LABELS[s] ?? s)} />
            </Block>

            <Block label="Logo">
              {brief.logoUrl
                ? <img src={brief.logoUrl} alt="Logo"
                    className="h-20 max-w-sm object-contain rounded-xl border border-slate-200 bg-white p-2 mt-1" />
                : <p className="text-sm text-slate-300 italic">No logo uploaded — to be designed</p>}
            </Block>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Block label="Main colour">
                {brief.brandColourPrimary
                  ? <div className="flex items-center gap-2 mt-1">
                      <div className="w-8 h-8 rounded-lg border border-slate-200 flex-shrink-0"
                        style={{ backgroundColor: brief.brandColourPrimary }} />
                      <span className="text-sm font-mono text-slate-700">{brief.brandColourPrimary}</span>
                    </div>
                  : <p className="text-sm text-slate-300 italic">Not chosen</p>}
              </Block>
              <Block label="Second colour">
                {brief.brandColourSecondary
                  ? <div className="flex items-center gap-2 mt-1">
                      <div className="w-8 h-8 rounded-lg border border-slate-200 flex-shrink-0"
                        style={{ backgroundColor: brief.brandColourSecondary }} />
                      <span className="text-sm font-mono text-slate-700">{brief.brandColourSecondary}</span>
                    </div>
                  : <p className="text-sm text-slate-300 italic">Not chosen</p>}
              </Block>
            </div>
          </section>

          <Divider />

          {/* ── 5. Pages & Features ────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Pages & Features</h3>
            <Block label="Pages needed">
              <Chips items={(brief.pagesNeeded ?? []).map(p => PAGE_LABELS[p] ?? p)} />
            </Block>
            <Block label="Special features">
              <Chips items={(brief.features ?? []).map(f => FEATURE_LABELS[f] ?? f)} />
            </Block>
          </section>

          <Divider />

          {/* ── 6. Content & Photos ────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Content & Photos</h3>
            <div className="grid grid-cols-1 gap-4">
              <Block label="Photos situation">
                <Val v={brief.hasPhotos ? (PHOTOS_LABELS[brief.hasPhotos] ?? brief.hasPhotos) : null} />
              </Block>
              <Block label="Content / copywriting">
                <Val v={brief.hasContent ? (CONTENT_LABELS[brief.hasContent] ?? brief.hasContent) : null} />
              </Block>
            </div>
          </section>

          <Divider />

          {/* ── 7. Social Media ────────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Social Media</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Block label="Facebook"><Val v={brief.socialFacebook} /></Block>
              <Block label="Instagram"><Val v={brief.socialInstagram} /></Block>
              <Block label="TikTok"><Val v={brief.socialTiktok} /></Block>
              <Block label="Other"><Val v={brief.socialOther} /></Block>
            </div>
          </section>

          <Divider />

          {/* ── 8. Web Address ─────────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Web Address (Domain)</h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <Block label="Has existing domain?">
                <Val v={brief.hasExistingWebsite ? "Yes" : "No"} />
              </Block>
              <Block label={brief.hasExistingWebsite ? "Existing domain URL" : "Preferred domain name"}>
                <Val v={brief.hasExistingWebsite ? brief.existingWebsiteUrl : brief.preferredDomain} />
              </Block>
            </div>
          </section>

          <Divider />

          {/* ── 9. Timeline & Extra ────────────────────────────── */}
          <section className="space-y-4">
            <h3 className="font-bold text-slate-700">Timeline & Extra Notes</h3>
            <Block label="Urgency / timeline">
              <Val v={brief.timeline ? (TIMELINE_LABELS[brief.timeline] ?? brief.timeline) : null} />
            </Block>
            <Block label="Anything else they want us to know">
              {brief.additionalInfo
                ? <p className="text-sm text-slate-800 whitespace-pre-wrap bg-slate-50 rounded-xl p-4 leading-relaxed">{brief.additionalInfo}</p>
                : <p className="text-sm text-slate-300 italic">Nothing added</p>}
            </Block>
          </section>
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-slate-200 px-6 py-4 flex gap-3">
          <a href={`mailto:${brief.contactEmail}`} className="flex-1">
            <Button variant="outline" className="w-full gap-2">
              <Mail className="w-4 h-4" /> Email {brief.contactName.split(" ")[0]}
            </Button>
          </a>
          <a href={`tel:${brief.contactPhone}`} className="flex-1">
            <Button className="w-full gap-2">
              <Phone className="w-4 h-4" /> Call {brief.contactName.split(" ")[0]}
            </Button>
          </a>
        </div>
      </div>
    </div>
  );
}

// ── List card ──────────────────────────────────────────────────────────────────

function BriefCard({ brief, onClick }: { brief: Brief; onClick: () => void }) {
  const links: InspirationLink[] = (() => {
    try { return brief.inspirationLinks ? JSON.parse(brief.inspirationLinks) as InspirationLink[] : []; }
    catch { return []; }
  })();

  return (
    <button type="button" onClick={onClick}
      className="w-full text-left bg-white rounded-2xl border border-slate-200 p-5 hover:border-primary/40 hover:shadow-sm transition-all space-y-3">

      {/* Top row */}
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h2 className="font-bold text-slate-800">{brief.businessName}</h2>
            <span className={`px-2 py-0.5 rounded-full text-xs font-semibold border ${STATUS_STYLES[brief.status] ?? "bg-slate-100 text-slate-600 border-slate-300"}`}>
              {brief.status.replace("_", " ")}
            </span>
          </div>
          <p className="text-sm text-slate-500 mt-0.5">{brief.businessType}</p>
        </div>
        <div className="text-right flex-shrink-0">
          {brief.logoUrl && (
            <img src={brief.logoUrl} alt="" className="h-10 w-auto object-contain rounded border border-slate-100 bg-white p-1 mb-1 ml-auto" />
          )}
          <p className="text-xs text-slate-400">{fmtDate(brief.createdAt)}</p>
        </div>
      </div>

      {/* Contact */}
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-slate-600">
        <span className="flex items-center gap-1"><Mail className="w-3.5 h-3.5 text-slate-400" />{brief.contactName}</span>
        <span className="flex items-center gap-1"><Phone className="w-3.5 h-3.5 text-slate-400" />{brief.contactPhone}</span>
        <span className="text-slate-400 text-xs">{brief.contactEmail}</span>
      </div>

      {/* Pages & features chips */}
      {((brief.pagesNeeded?.length ?? 0) > 0 || (brief.features?.length ?? 0) > 0) && (
        <div className="flex flex-wrap gap-1.5">
          {(brief.pagesNeeded ?? []).slice(0, 5).map(p => (
            <span key={p} className="px-2 py-0.5 rounded-full text-xs font-medium bg-slate-100 text-slate-600">
              {PAGE_LABELS[p] ?? p}
            </span>
          ))}
          {(brief.features ?? []).map(f => (
            <span key={f} className="px-2 py-0.5 rounded-full text-xs font-medium bg-primary/10 text-primary">
              {FEATURE_LABELS[f] ?? f}
            </span>
          ))}
          {(brief.pagesNeeded?.length ?? 0) > 5 && (
            <span className="text-xs text-slate-400">+{(brief.pagesNeeded?.length ?? 0) - 5} pages</span>
          )}
        </div>
      )}

      {/* Brand colours + metadata */}
      <div className="flex items-center gap-3 text-xs text-slate-400 flex-wrap">
        {(brief.brandColourPrimary || brief.brandColourSecondary) && (
          <div className="flex gap-1">
            {brief.brandColourPrimary && (
              <div className="w-4 h-4 rounded-full border border-slate-200"
                style={{ backgroundColor: brief.brandColourPrimary }} title={brief.brandColourPrimary} />
            )}
            {brief.brandColourSecondary && (
              <div className="w-4 h-4 rounded-full border border-slate-200"
                style={{ backgroundColor: brief.brandColourSecondary }} title={brief.brandColourSecondary} />
            )}
          </div>
        )}
        {links.length > 0 && <span>{links.length} inspiration link{links.length !== 1 ? "s" : ""}</span>}
        {brief.timeline && <span>{TIMELINE_LABELS[brief.timeline] ?? brief.timeline}</span>}
        {brief.mainGoal && <span>{GOAL_LABELS[brief.mainGoal] ?? brief.mainGoal}</span>}
      </div>
    </button>
  );
}

// ── Page ───────────────────────────────────────────────────────────────────────

export default function AdminWebsiteBriefs() {
  const { user, isLoading: authLoading } = useAuth();
  const [briefs, setBriefs] = useState<Brief[]>([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Brief | null>(null);

  useEffect(() => {
    if (user?.role !== "admin") return;
    fetch("/api/admin/free-website-applications", {
      headers: { Authorization: `Bearer ${token()}` },
    })
      .then(r => r.json() as Promise<{ applications: Brief[] }>)
      .then(d => setBriefs(d.applications ?? []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user]);

  function handleStatusChange(id: number, status: string) {
    setBriefs(prev => prev.map(b => b.id === id ? { ...b, status } : b));
    setSelected(prev => prev?.id === id ? { ...prev, status } : prev);
  }

  if (authLoading) return null;
  if (user?.role !== "admin") return <Redirect to="/" />;

  return (
    <AdminLayout current="/admin/website-briefs">
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-800">Website Briefs</h1>
            <p className="text-slate-500 text-sm mt-0.5">Submitted via ayrshirehub.co.uk/free-website</p>
          </div>
          <span className="text-3xl font-black text-primary">{briefs.length}</span>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
          </div>
        ) : briefs.length === 0 ? (
          <div className="text-center py-20 text-slate-400 space-y-2">
            <FileText className="w-12 h-12 mx-auto opacity-30" />
            <p className="font-medium">No briefs submitted yet</p>
            <p className="text-sm">Share the link and they'll appear here.</p>
          </div>
        ) : (
          <div className="space-y-3">
            {briefs.map(b => (
              <BriefCard key={b.id} brief={b} onClick={() => setSelected(b)} />
            ))}
          </div>
        )}
      </div>

      {selected && (
        <BriefDetail
          brief={selected}
          onClose={() => setSelected(null)}
          onStatusChange={handleStatusChange}
        />
      )}
    </AdminLayout>
  );
}
